/**
 * auth-gate.ts — the portal's branded sign-in / no-access gate.
 * =====================================================================
 * The portal authenticates with the same-origin Django **session cookie** (set
 * by /accounts/login/). It does NOT run its own login form — re-implementing
 * allauth (password + Google/Microsoft SSO + CSRF) in the SPA would be fragile.
 * Instead, when an API call comes back unauthorized we show a full-screen branded
 * gate with two explicit choices, and let Django own the actual credential step:
 *
 *   • Sign in  → full-page nav to /accounts/login/?next=<here> (password or SSO);
 *                allauth honours `next`, so the user returns to the portal.
 *   • Sign out → POST /accounts/logout/ (CSRF), then back to the login page — for
 *                the "signed in with the wrong (non-provider) account" case.
 *
 * WHY A GATE INSTEAD OF AN AUTO-REDIRECT: DRF SessionAuthentication returns 403
 * for BOTH "not logged in" AND "logged in but not a Provider". A silent redirect
 * to login looped forever for the second case (login → back → 403 → login …).
 * A static gate cannot loop (it navigates only on an explicit click) and it gives
 * the wrong-account user an obvious way out. It also fixes the "I can't find a
 * login link" gap — the sign-in affordance is now visible and branded.
 *
 * CSP-safe: wired from this bundled module via a delegated listener; no inline JS.
 */

/** Read a cookie value by name (csrftoken is not httpOnly, by design). */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(";")) {
    const c = part.trim();
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length));
  }
  return null;
}

/** Where to return after authenticating: the portal location we're sitting on. */
function currentNext(): string {
  return encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
}

/** Reveal the branded gate overlay (idempotent). */
export function showAuthGate(): void {
  const gate = document.getElementById("auth-gate");
  if (gate) gate.style.display = "flex";
}

/** Hide the gate (used once an authenticated request succeeds). */
export function hideAuthGate(): void {
  const gate = document.getElementById("auth-gate");
  if (gate) gate.style.display = "none";
}

function signIn(): void {
  window.location.assign(`/accounts/login/?next=${currentNext()}`);
}

async function signOut(): Promise<void> {
  // allauth logout is a POST; include the CSRF header like any unsafe request.
  const csrf = readCookie("csrftoken");
  try {
    await fetch("/accounts/logout/", {
      method: "POST",
      credentials: "same-origin",
      headers: csrf ? { "X-CSRFToken": csrf } : {},
    });
  } catch {
    // Ignore network errors — we navigate to login regardless so the user is
    // never stranded.
  }
  window.location.assign(`/accounts/login/?next=${currentNext()}`);
}

/**
 * Wire the gate's Sign in / Sign out controls AND the sidebar Sign out link via a
 * single delegated listener on the document. Called once at startup from main.ts.
 */
export function initAuthGate(): void {
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-signin]")) {
      e.preventDefault();
      signIn();
    } else if (target.closest("[data-signout]")) {
      e.preventDefault();
      void signOut();
    }
  });
}
